/**
 * The feed frame (WORKBOARD I9; decision 88: plain, "not greebly"): the one overlay both live
 * transmissions wear — the drone feed (ui/droneFeed.ts) and the missile cam (ui/missileCam.ts).
 * A thin frame with corner brackets, a live dot and a title, four readout corners, a small reticle,
 * a centred message, a canvas for boxes drawn over the scene, and a touch of desaturation on the
 * game's canvas while it is up. Nothing else. Presentation only.
 */
import * as THREE from 'three';

export type FeedCorner = 'tl' | 'tr' | 'bl' | 'br';

const _p = new THREE.Vector3();

export class FeedFrame {
  readonly el: HTMLDivElement;
  private readonly marks: HTMLCanvasElement;
  private readonly g: CanvasRenderingContext2D;
  private readonly corners: Record<FeedCorner, HTMLElement>;
  private readonly msg: HTMLElement;
  private shown = false;

  /** `kind` names the body class (`<kind>-on`) other HUDs can step back behind. */
  constructor(private readonly canvas: HTMLCanvasElement, private readonly kind: string) {
    this.el = document.createElement('div');
    this.el.className = 'feed-frame';
    this.el.innerHTML = `
      <style>
        .feed-frame { position: fixed; inset: 0; z-index: 45; pointer-events: none; display: none; font: 600 12px/1.3 ui-monospace, SFMono-Regular, Menlo, monospace; color: #e6ecef; letter-spacing: 0.06em; }
        .feed-frame .frame { position: absolute; inset: 18px; border: 1px solid rgba(230,236,239,0.28); }
        .feed-frame .br { position: absolute; width: 26px; height: 26px; border-color: rgba(230,236,239,0.85); border-style: solid; }
        .feed-frame .c1 { left: 18px; top: 18px; border-width: 2px 0 0 2px; } .feed-frame .c2 { right: 18px; top: 18px; border-width: 2px 2px 0 0; }
        .feed-frame .c3 { left: 18px; bottom: 18px; border-width: 0 0 2px 2px; } .feed-frame .c4 { right: 18px; bottom: 18px; border-width: 0 2px 2px 0; }
        .feed-frame .ro { position: absolute; text-shadow: 0 1px 2px rgba(0,0,0,0.7); white-space: nowrap; }
        .feed-frame .tl { left: 32px; top: 28px; } .feed-frame .tr { right: 32px; top: 28px; text-align: right; }
        .feed-frame .bl { left: 32px; bottom: 28px; } .feed-frame .brr { right: 32px; top: 48px; text-align: right; }   /* the corner map owns the bottom right */
        .feed-frame .live { display: inline-block; width: 8px; height: 8px; border-radius: 50%; background: #e05a4a; margin-right: 6px; vertical-align: 0; }
        .feed-frame .bar { display: inline-block; width: 64px; height: 6px; border: 1px solid rgba(230,236,239,0.7); margin-left: 8px; vertical-align: 1px; }
        .feed-frame .bar i { display: block; height: 100%; background: #e6ecef; }
        .feed-frame .ret { position: absolute; left: 50%; top: 50%; width: 18px; height: 18px; margin: -9px 0 0 -9px; }
        .feed-frame .ret::before, .feed-frame .ret::after { content: ''; position: absolute; background: rgba(230,236,239,0.8); }
        .feed-frame .ret::before { left: 8px; top: 0; width: 2px; height: 18px; } .feed-frame .ret::after { top: 8px; left: 0; height: 2px; width: 18px; }
        .feed-frame .msg { position: absolute; left: 50%; top: 42%; transform: translateX(-50%); font-size: 20px; letter-spacing: 0.2em; color: #f0c674; display: none; white-space: nowrap; }
        .feed-frame canvas { position: absolute; inset: 0; }
        /* in the turn game the title and the team panels own the top edge: the missile cam's top readouts sit below them */
        body.missile-cam-on .feed-frame .tl { top: 118px; } body.missile-cam-on .feed-frame .tr { top: 118px; } body.missile-cam-on .feed-frame .brr { top: 138px; }
        /* the soldier's HUD steps back while a feed is up, never away (67.35, lane E's mock): the match, the corner map and
           your health stay — you are standing in the open; the aim, the slot, the keys, what you hold and the message go */
        body.drone-feed-on #ground-hud .gh-xh, body.drone-feed-on #ground-hud .gh-dot, body.drone-feed-on #ground-hud .gh-slot, body.drone-feed-on #ground-hud .gh-keys-wrap, body.drone-feed-on #ground-hud .gh-hands-wrap, body.drone-feed-on #ground-hud .gh-msg, body.drone-feed-on #action-hud .help { display: none !important; }
      </style>
      <canvas></canvas>
      <div class="frame"></div><div class="br c1"></div><div class="br c2"></div><div class="br c3"></div><div class="br c4"></div>
      <div class="ro tl"></div><div class="ro tr"></div><div class="ro bl"></div><div class="ro brr"></div>
      <div class="ret"></div><div class="msg"></div>`;
    document.body.appendChild(this.el);
    this.marks = this.el.querySelector('canvas') as HTMLCanvasElement;
    this.g = this.marks.getContext('2d')!;
    const q = (s: string) => this.el.querySelector(s) as HTMLElement;
    this.corners = { tl: q('.tl'), tr: q('.tr'), bl: q('.bl'), br: q('.brr') };
    this.msg = q('.msg');
  }

  get visible(): boolean { return this.shown; }

  show(): void {
    if (this.shown) return;
    this.shown = true;
    this.el.style.display = 'block';
    this.msg.style.display = 'none';
    this.canvas.style.filter = 'saturate(0.55) contrast(1.06)';
    document.body.classList.add(`${this.kind}-on`);
  }

  hide(): void {
    if (!this.shown) return;
    this.shown = false;
    this.el.style.display = 'none';
    this.msg.style.display = 'none';
    this.canvas.style.filter = '';
    document.body.classList.remove(`${this.kind}-on`);
  }

  /** A corner's text (HTML allowed: the live dot is `<span class="live"></span>`, a bar `<span class="bar"><i style="width:N%"></i></span>`). */
  readout(corner: FeedCorner, html: string): void { this.corners[corner].innerHTML = html; }

  /** The centred message, or none. */
  message(text: string | null): void {
    this.msg.textContent = text ?? '';
    this.msg.style.display = text ? 'block' : 'none';
  }

  /** Start a frame of boxes: clears the canvas (sized to the window). */
  beginMarks(): void {
    if (this.marks.width !== innerWidth || this.marks.height !== innerHeight) { this.marks.width = innerWidth; this.marks.height = innerHeight; }
    this.g.clearRect(0, 0, this.marks.width, this.marks.height);
  }

  /**
   * Box a world point as seen by `camera`: `size` world units across, labelled, at `alpha`.
   * Returns false when it is off screen or behind the camera. Call beginMarks first.
   */
  box(camera: THREE.Camera, point: { x: number; y: number; z: number }, size: number, label: string, alpha = 1, color = '#f0c674'): boolean {
    _p.set(point.x, point.y, point.z);
    const dist = _p.distanceTo(camera.position);
    _p.project(camera);
    if (_p.z > 1 || Math.abs(_p.x) > 1.05 || Math.abs(_p.y) > 1.05) return false;
    const x = (_p.x + 1) / 2 * innerWidth, y = (1 - _p.y) / 2 * innerHeight;
    const fov = (camera as THREE.PerspectiveCamera).fov ?? 55;
    const px = Math.max(14, (size / dist) * innerHeight / (2 * Math.tan((fov * Math.PI) / 360)));
    const g = this.g;
    g.globalAlpha = alpha;
    g.strokeStyle = color; g.lineWidth = 1.5;
    g.strokeRect(x - px / 2, y - px * 0.75, px, px);
    g.font = '600 10px ui-monospace, Menlo, monospace'; g.fillStyle = color;
    g.fillText(label, x - px / 2, y - px * 0.75 - 4);
    g.globalAlpha = 1;
    return true;
  }

  dispose(): void { this.hide(); this.el.remove(); }
}
